import { Container } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React from 'react';
import Breadcrumbs from '../components/breadcrumbs/Breadcrumbs';
import { UmbracoRouteNamesProvider } from '../components/breadcrumbs/UmbracoRouteNamesContext';
import { getCampaigns, GetFacilities } from '../helpers/coreApi';
import buildFacilityTree from '../helpers/facilityHelper';
import {
  getBottomMenu,
  GetFacilitiesContentNew as GetFacilitiesFromUmbraco,
  getFacilitiesArchiveContent,
  getFlattenedUmbracoRouteNames,
  getFooterMenu,
  getFooterText,
  UmbracoRouteNameItem,
} from '../helpers/umbracoApi';
import { CampaignsDto, FacilityDto } from '../types/generic';
import {
  FacilitiesArchiveContent,
  FacilityTreeNode,
  MainMenu,
} from '../types/umbraco';
import Content from './content/content';
import Footer from './footer/footer';
import Header from './header/header';
import styles from './layout.module.css';

const SafeContainer = Container as React.ComponentType<any>;

export interface SharedPageProps {
  facilities?: FacilityDto[];
  facilityTree?: FacilityTreeNode[];
  footerMenu?: MainMenu;
  bottomMenu?: MainMenu;
  footerText?: string;
  headerMenu?: MainMenu;
  campaigns?: CampaignsDto[];
  umbracoRouteNames?: UmbracoRouteNameItem[];
  facilitiesArchiveContent?: FacilitiesArchiveContent | null;
}

interface LayoutProps extends SharedPageProps {
  children?: React.ReactNode;
}

function getFacilityMenuItems(nodes: FacilityTreeNode[]): FacilityTreeNode[] {
  return nodes.flatMap((node) => {
    const childFacilities = getFacilityMenuItems(node.children || []);

    return node.isFacility ? [node, ...childFacilities] : childFacilities;
  });
}

export function Layout(layoutProps: LayoutProps): JSX.Element {
  const { children, ...globalProps } = layoutProps;
  const isStaging = process.env.NEXT_PUBLIC_ENV === 'staging';
  const router = useRouter();
  const showGlobalBreadcrumbs = false;
  const umbracoRouteNames = globalProps.umbracoRouteNames || [];
  return (
    <>
      <div className={`${styles.container} ${isStaging && styles.devBorder}`}>
        <UmbracoRouteNamesProvider value={umbracoRouteNames}>
          <a className={styles.skipLink} href="#main-content">
            Hoppa till huvudinnehåll
          </a>
          <Header {...globalProps} />
          {showGlobalBreadcrumbs && (
            <SafeContainer maxW="7xl" pt={4} pb={2}>
              <Breadcrumbs path={router.asPath} />
            </SafeContainer>
          )}
          <Content>{children}</Content>
          <Footer {...globalProps} />
        </UmbracoRouteNamesProvider>
      </div>
    </>
  );
}

export async function getSharedPageProps(): Promise<
  Record<string, SharedPageProps>
> {
  const [
    facilities,
    umbracoFacilities,
    footerMenu,
    bottomMenu,
    footerText,
    umbracoRouteNames,
    campaigns,
    facilitiesArchiveContent,
  ] = await Promise.all([
    GetFacilities(),
    GetFacilitiesFromUmbraco(),
    getFooterMenu(),
    getBottomMenu(),
    getFooterText(),
    getFlattenedUmbracoRouteNames(),
    getCampaigns(),
    getFacilitiesArchiveContent(),
  ]);
  // No type filter: county/city pages must stay in the tree even when they
  // have zero (or wrongly typed) facility children, so the empty-state
  // message can still render for them.
  const facilityTree = buildFacilityTree(umbracoFacilities.facilities || []);
  const facilityMenuItems = getFacilityMenuItems(facilityTree);

  const headerMenu = {
    items: [
      // { name: 'Vanliga frågor', path: '/vanliga-fragor' },
      {
        name: 'Hyr förråd',
        path: '/hyr-forrad/',
        children: facilityMenuItems.sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      },
      { name: 'Om oss', path: '/om-mitt-lager' },
      // { name: 'Kontakt', path: '/kontakt' },
      // { name: 'Tips & Råd', path: '/tips-rad' },
      { name: 'Kundservice', path: '/kontakt' },
    ],
  };

  // wrap in object property so it's easier to add.
  // now you can just do:
  // const whatEver = await getSharedPageProps();
  // props: {
  //    ...whatEver,
  // },
  return {
    sharedPageProps: {
      facilities,
      facilityTree,
      footerMenu,
      bottomMenu,
      footerText,
      headerMenu,
      campaigns,
      umbracoRouteNames,
      facilitiesArchiveContent,
    },
  };
}
